import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Row, Typography, Tabs, Col, Drawer, Grid } from 'antd';

import { useTranslation } from 'react-i18next';
import DeviceActionsMenu from '@components/DeviceActionsMenu';

import DeviceActionModal from '@components/DeviceActionModal';
import Transactions from '@pages/Assets/Transactions';
import SessionContext from '@providers/Session/SessionContext';
import CoverageMap from '@components/Maps/CoverageMap';
import Sessions from '@pages/Assets/Sessions';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';

import ActionTypes from '@utils/deviceActions';
import { useDeviceFormatter } from '@hooks/useDeviceFormatter';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import DeviceActionsTimeline from '@components/DeviceActionsTimeline';
import AlertsEvents from '@pages/Alerts/Events';
import ActionsModalByTypes from '@components/Actions/ActionsModal/ActionsModalByTypes';
import Actions from '@components/Actions';
import CustomCard from '@components/CustomCard';

import { IDevice } from '../types';
import TabPanesDetails from './TabPanesDetails';

import { Container } from './styles';
import TabPanesAdvanced from './TabPanesAdvanced';
import TagDiagnostics from './TagDiagnostics';

const { Title } = Typography;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

interface IPreviewProps {
  deviceProps: IDevice;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showContracted?: string;
  handleMutateInventoryData: any;
  isRoot: boolean;
  stock?: 'true' | 'false';
}

const InventoryPreview = ({
  deviceProps,
  isOpen,
  setIsOpen,
  showContracted,
  handleMutateInventoryData,
  isRoot,
  stock,
}: IPreviewProps) => {
  const { t } = useTranslation('inventory-detailed');
  const {
    effectiveRoles,
    hasPermission,
    selectedApp,
    isMot,
    isMotPay,
    customer: { isCustomerFinanciallyBlocked },
  } = useContext(SessionContext);
  const screens = useBreakpoint();

  const [actionRole, setActionRole] = useState<RolesTypes>();
  const [modalDeviceActionsVisible, setModalDeviceActionsVisible] =
    useState<boolean>(false);
  const [modalDeviceActionsVisibleMot, setModalDeviceActionsVisibleMot] =
    useState<boolean>(false);
  const [actionToExecute, setActionToExecute] = useState<ActionTypes>();

  const isConnectOne = selectedApp === ApplicationsConstants.CONNECT_ONE;

  const { device, handleUpdateNickname, handleUpdateNote } = useDeviceFormatter(
    deviceProps.id,
    showContracted,
    handleMutateInventoryData,
  );

  const handleActionClick = useCallback((key: any) => {
    if (key === ActionTypes.CHANGE_SIMCARD) {
      setActionRole(rolesConstants.CHANGE_SIMCARD);
      setModalDeviceActionsVisibleMot(true);
      return;
    }
    setActionToExecute(Number(key));
    setModalDeviceActionsVisible(true);
  }, []);

  const allDetailsRoles = useMemo(
    (): RolesTypes[] => [
      rolesConstants.INVENT_STATUS,
      rolesConstants.INVENT_OPERATOR,
      rolesConstants.INVENT_LAST_CONNECTION,
      rolesConstants.INVENT_ONLINE,
      rolesConstants.INVENT_CITY,
      rolesConstants.INVENT_ACTIVATION_DATE,
      rolesConstants.INVENT_PLAN,
      rolesConstants.INVENT_CONSUMPTION,
      rolesConstants.INVENT_ICCID,
      rolesConstants.INVENT_IMEI,
      rolesConstants.INVENT_MSISDN,
      rolesConstants.INVENT_IMSI,
      rolesConstants.INVENT_OBSERVATION,
    ],
    [],
  );

  const hasAnyDetailsRole = useMemo(
    () =>
      effectiveRoles?.inventory?.some(roleName =>
        allDetailsRoles.includes(roleName),
      ),
    [allDetailsRoles, effectiveRoles?.inventory],
  );

  const canNicknameChange = useMemo(
    () =>
      hasPermission(rolesConstants.NICKNAME_CHANGE) &&
      (!isMot || showContracted === 'true'),
    [hasPermission, isMot, showContracted],
  );

  return (
    <Drawer
      width={screens?.xs ? '90%' : '60%'}
      placement="right"
      closable={false}
      onClose={() => setIsOpen(false)}
      destroyOnClose
      open={isOpen}
      className="preview-drawer"
    >
      <Container>
        <Row
          gutter={[16, 16]}
          justify="space-between"
          className="ip-header"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title
            level={4}
            editable={
              canNicknameChange
                ? {
                    onChange: handleUpdateNickname,
                  }
                : false
            }
            style={{ flex: 1 }}
          >
            {device?.nickname}
          </Title>

          {isMot ? (
            !isCustomerFinanciallyBlocked && (
              <Actions.Menu
                isStock={stock === 'true'}
                showContracted={showContracted}
                onActionClick={(role: RolesTypes) => {
                  setActionRole(role);
                  setModalDeviceActionsVisibleMot(true);
                }}
              />
            )
          ) : (
            <DeviceActionsMenu
              onActionClick={({ key }) => handleActionClick(key)}
            />
          )}
        </Row>
        <Tabs defaultActiveKey="1">
          {hasAnyDetailsRole && (
            <TabPane tab={t('preview-tabs.details')} key="1">
              <TabPanesDetails
                device={deviceProps}
                handleUpdateNote={handleUpdateNote}
                observation={device?.observation}
                isConnectOne={isConnectOne}
                showContracted={showContracted}
              />
            </TabPane>
          )}
          {hasPermission(rolesConstants.INVENT_LOCATION_INFO, 'inventory') &&
            device?.latitude &&
            device?.longitude && (
              <TabPane tab={t('preview-tabs.location')} key="2">
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <div
                      style={{
                        height: 400,
                        borderRadius: 5,
                        overflow: 'hidden',
                      }}
                    >
                      <CoverageMap
                        mapHeight={580}
                        dataProps={
                          device?.latitude || device?.longitude
                            ? [
                                {
                                  id: `${device?.id}`,
                                  lat: Number(device.latitude),
                                  lng: Number(device?.longitude),
                                },
                              ]
                            : undefined
                        }
                        allowSearch
                        allowCoverage
                      />
                    </div>
                  </Col>
                </Row>
              </TabPane>
            )}

          {hasPermission(rolesConstants.VIEW_AUTHENTICATIONS) && !isRoot && (
            <TabPane tab={t('preview-tabs.sessions')} key="3">
              <Row gutter={[24, 24]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <Sessions
                    tableOnly
                    onlyMsisdn={Number(deviceProps?.msisdn)}
                  />
                </Col>
              </Row>
            </TabPane>
          )}
          {isMotPay && hasPermission(rolesConstants.VIEW_TPDU) && (
            <TabPane tab={t('preview-tabs.transactions')} key="4">
              <Row gutter={[24, 24]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <Transactions imsi={Number(deviceProps?.imsi)} />
                </Col>
              </Row>
            </TabPane>
          )}

          {hasPermission(rolesConstants.ACTIONS_QUEUE) && (
            <TabPane tab={t('preview-tabs.commands')} key="5">
              <Row gutter={[24, 24]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <DeviceActionsTimeline id={deviceProps?.id} />
                </Col>
              </Row>
            </TabPane>
          )}

          {hasPermission(rolesConstants.VIEW_SUBSCRIBER_INFO) && !isMot && (
            <TabPane
              forceRender
              tab={t('advanced-informations.tab-title')}
              key="6"
            >
              <Row gutter={[24, 24]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <TabPanesAdvanced
                    customerId={deviceProps?.customerId}
                    msisdn={deviceProps?.msisdn}
                    imsi={deviceProps?.imsi}
                  />
                </Col>
              </Row>
            </TabPane>
          )}

          {hasPermission(rolesConstants.VIEW_DIAGNOSTICS) && isMot && (
            <TabPane forceRender tab={t('preview-tabs.diagnostics')} key="6">
              <Row gutter={[24, 24]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <CustomCard loading={!device} bodyStyle={{ padding: 0 }}>
                    <TagDiagnostics msisdn={Number(device?.msisdn)} />
                  </CustomCard>
                </Col>
              </Row>
            </TabPane>
          )}
          {hasPermission(rolesConstants.VIEW_ALERTS) && isMot && (
            <TabPane tab={t('preview-tabs.alerts')} key="7">
              <Row gutter={[16, 16]}>
                <Col lg={24} md={24} xl={24} xs={24}>
                  <CustomCard loading={!device} bodyStyle={{ padding: 0 }}>
                    <AlertsEvents
                      defaultFilters={{
                        msisdn: device?.msisdn,
                        showContracted,
                      }}
                      tableOnly
                    />
                  </CustomCard>
                </Col>
              </Row>
            </TabPane>
          )}
        </Tabs>
        <ActionsModalByTypes.Simcard
          visible={modalDeviceActionsVisibleMot}
          onClose={() => setModalDeviceActionsVisibleMot(false)}
          actionRole={actionRole}
          selectedDevices={device && [device]}
        />

        <DeviceActionModal
          visible={modalDeviceActionsVisible}
          actionKey={actionToExecute}
          devicesProps={device && [device]}
          onClose={() => setModalDeviceActionsVisible(false)}
        />
      </Container>
    </Drawer>
  );
};

export default InventoryPreview;
